import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Broker } from "../../../../api/Api";
import dayjs from "dayjs";
import { mergerSummaryRows } from "../../../mergers/mergerSummaryRows";
import { operationalReadinessPdfRows } from "../../../../components/lifecycle/signOff/operationalReadiness";

type Row = [string, string];

const fmt = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const section = (
  doc: jsPDF,
  title: string,
  rows: Row[],
  startY: number
): number => {
  autoTable(doc, {
    head: [[title, ""]],
    body: rows.filter((row) => row[0] !== "" && row[1] !== ""),
    startY,
    theme: "grid",
    headStyles: { fillColor: [18, 46, 56], textColor: 255, fontStyle: "bold" },
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: "bold" },
      1: { cellWidth: 110 }
    }
  });
  const finalY = (doc as unknown as { lastAutoTable: { finalY: number } })
    .lastAutoTable.finalY;
  return finalY + 6;
};

/**
 * The full broker record as a PDF, mirroring the counterparty and country documents. Unlike the
 * on-screen overview panel, this carries the per-field detail of every lifecycle, which is the
 * whole reason the download exists.
 */
export const buildSignOffPdf = (broker: Broker): jsPDF => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const margin = 14;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(
    `Broker: ${broker.brokerName || ""}${broker.isMerger ? " (MERGER)" : ""}`,
    margin,
    margin + 4
  );

  let y = margin + 12;

  y = section(
    doc,
    "Basic Information",
    [
      ["Trading team", fmt(broker.tradingTeam)],
      ["Contracting Entity", fmt(broker.contractingEntity)],
      ["Requester", fmt(broker.contactPersonTradingTeam)],
      ["Broker contact", fmt(broker.brokerContactName)],
      ["Broker contact email", fmt(broker.brokerContactEmail)],
      ["Product Type", fmt(broker.productType)],
      ["Venue", fmt(broker.venue)],
      ["Settlement type", fmt(broker.tradingSettlementType)],
      ["Agreement type", fmt(broker.agreementType)],
      ["Transaction type", fmt(broker.transactionType)],
      ["Deal Unit", fmt(broker.unit)],
      [
        "Expected deal volume/size",
        broker.dealVolume ? `${broker.dealVolume} ${broker.unit || ""}` : "N/A"
      ],
      broker.marketAccessLifecycle?.signOffNote
        ? ["Sign-off note", fmt(broker.marketAccessLifecycle?.signOffNote)]
        : ["", ""]
    ],
    y
  );

  if (broker.isMerger) {
    y = section(
      doc,
      "Merger",
      mergerSummaryRows(
        "broker",
        broker.mergerDescription,
        broker.marketAccessLifecycle
      ),
      y
    );
  }

  const compliance = broker.complianceLifecycle;
  y = section(
    doc,
    "Compliance",
    [
      ["KYC & Sanction screening", fmt(compliance?.kycCheck)],
      ["KYC Check on MFT", fmt(compliance?.kycCheckMft)],
      ["REMIT reporting", fmt(compliance?.remitReporting)],
      ["Reporting setup", fmt(compliance?.reportingSetup)],
      compliance?.signOffNote
        ? ["Sign-off note", fmt(compliance?.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const legal = broker.legalLifecycle;
  y = section(
    doc,
    "Legal",
    [
      ["Agreement review", fmt(legal?.agreementReview)],
      ["NDA Review", fmt(legal?.ndaReview)],
      ["Clear Contract case", fmt(legal?.clearContractCase)],
      legal?.signOffNote ? ["Sign-off note", fmt(legal?.signOffNote)] : ["", ""]
    ],
    y
  );

  const risk = broker.riskLifecycle;
  y = section(
    doc,
    "Risk",
    [
      ["Mandate review", fmt(risk?.mandateReview)],
      risk?.signOffNote ? ["Sign-off note", fmt(risk?.signOffNote)] : ["", ""]
    ],
    y
  );

  const creditRisk = broker.creditRiskLifecycle;
  y = section(
    doc,
    "Credit Risk",
    [
      ["Credit check", fmt(creditRisk?.creditCheck)],
      ["Credit amount", fmt(creditRisk?.creditAmount)],
      ["Credit currency", fmt(creditRisk?.creditCurrency)],
      creditRisk?.signOffNote
        ? ["Sign-off note", fmt(creditRisk?.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const finance = broker.financeLifecycle;
  y = section(
    doc,
    "Finance",
    [
      ["Agreement review", fmt(finance?.agreementReview)],
      ["Broker setup in Business Central", fmt(finance?.brokerSetup)],
      ["IBAN", fmt(finance?.iban)],
      ["Callback", fmt(finance?.callback)],
      ["Trade Confirmation Setup", fmt(finance?.tradeConfirmationSetup)],
      finance?.signOffNote
        ? ["Sign-off note", fmt(finance?.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const signOff = broker.signOff;
  const readinessRows = operationalReadinessPdfRows(
    signOff?.operationalReadiness
  );
  if (readinessRows) {
    y = section(doc, "Operational readiness", readinessRows, y);
  }

  if (signOff?.summary) {
    y = section(doc, "Summary", [["", signOff.summary]], y);
  }

  if (signOff?.signatories?.length) {
    const statusRows: Row[] = [["Status", fmt(signOff.status)]];
    if (signOff.status === "rejected") {
      statusRows.push(["Rejected by", fmt(signOff.rejectedBy)]);
      if (signOff.rejectedAt) {
        statusRows.push([
          "Rejected at",
          dayjs(signOff.rejectedAt).format("LLL")
        ]);
      }
      statusRows.push(["Rejection reason", fmt(signOff.rejectionReason)]);
    }
    const signatoryRows: Row[] = signOff.signatories
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => [
        `${s.name}${s.isCeo ? " (CEO)" : ""}`,
        s.signed
          ? `Signed${s.signedAt ? ` on ${dayjs(s.signedAt).format("LLL")}` : ""}`
          : "Pending"
      ]);
    section(doc, "Sign off", [...statusRows, ...signatoryRows], y);
  }

  return doc;
};
